import type { QueryClient, QueryKey } from '@tanstack/vue-query';
import { SectionQueryKey, TypeQueryKey } from '@/modules/common/enums/query-key.enum.ts';
import { DOCUMENT_HEADER_COLOR_KEY } from '@/modules/document/consts/document-color.ts';
import { unref } from 'vue';
import {
  publishEntityUpdated,
  setupEntityUpdateSync,
  type ActiveEntityContext,
} from '@/modules/shared/composables/entity-update-sync.ts';

const ENTITY_TYPE = 'document';

const VIEWER_BASE_KEYS = new Set<string>([
  SectionQueryKey.Document,
  SectionQueryKey.Control,
  SectionQueryKey.Assignments,
  SectionQueryKey.AssignmentsSearch,
]);

const COLOR_BASE_KEYS = new Set<string>([
  SectionQueryKey.Document,
  SectionQueryKey.Control,
  SectionQueryKey.Assignments,
]);

interface DocumentUpdateSyncOptions {
  getActiveDocumentContext?: (documentId: number) => ActiveEntityContext | null;
}

function matchesDocumentUpdate(queryKey: QueryKey, documentId: number) {
  if (queryKey[0] === DOCUMENT_HEADER_COLOR_KEY) {
    return queryKey[2] === documentId;
  }

  const [, baseKey, typeKey, entityId] = queryKey;

  if (typeKey === TypeQueryKey.Viewer || typeKey === TypeQueryKey.Count) {
    return typeof baseKey === 'string' && VIEWER_BASE_KEYS.has(baseKey);
  }

  if (typeKey !== TypeQueryKey.Color || typeof baseKey !== 'string' || !COLOR_BASE_KEYS.has(baseKey)) {
    return false;
  }

  return entityId === undefined || unref(entityId) === documentId;
}

export function publishDocumentUpdated(documentId: number) {
  publishEntityUpdated(ENTITY_TYPE, documentId);
}

export function setupDocumentUpdateSync(queryClient: QueryClient, options: DocumentUpdateSyncOptions = {}) {
  return setupEntityUpdateSync(queryClient, {
    entityType: ENTITY_TYPE,
    matchesQuery: matchesDocumentUpdate,
    getActiveEntityContext: options.getActiveDocumentContext,
  });
}
